/**
 * Product card image carousel (CRO 0134).
 *
 * A tiny custom element that lets visitors browse a product's images straight from the
 * collection card via prev/next arrows, without opening the PDP. It is intentionally
 * dependency-free (no Splide/Swiper) and self-initialising: because it is a custom element,
 * its connectedCallback fires automatically for cards injected by Ajax filtering, sorting and
 * infinite scroll — no manual re-init hook needed.
 *
 * Markup (rendered by snippets/product-card.liquid):
 *   <product-card-carousel class="product-card__carousel">
 *     <a class="product-card__link">                <- click = go to PDP
 *       <img class="product-card__carousel-slide is-active">
 *       <img class="product-card__carousel-slide">  <- loading="lazy", hidden until shown
 *       ...
 *     </a>
 *     <button data-carousel-prev>…</button>          <- OUTSIDE the <a>: click = cycle, not navigate
 *     <button data-carousel-next>…</button>
 *   </product-card-carousel>
 */
(function () {
  if (customElements.get('product-card-carousel')) return;

  class ProductCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      if (this.querySelectorAll('.product-card__carousel-slide').length <= 1) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');

      this._handlePrev = this._navigate.bind(this, -1);
      this._handleNext = this._navigate.bind(this, 1);
      if (this.prevButton) this.prevButton.addEventListener('click', this._handlePrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this._handleNext);

      this.setAttribute('data-carousel-ready', '');
    }

    disconnectedCallback() {
      if (this.prevButton) this.prevButton.removeEventListener('click', this._handlePrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this._handleNext);
    }

    _navigate(direction, event) {
      // The arrows sit outside the <a>, but stop the event anyway so a click never bubbles to
      // any card-level handler or triggers the instant-page prefetch.
      event.preventDefault();
      event.stopPropagation();

      // Re-read the slides from the DOM on every click rather than caching them: the theme's
      // swatch handler (ProductCard._onSwatchChanged) can replaceWith() the primary <img>, which
      // would leave a cached list pointing at a detached node. Deriving the current index from the
      // live is-active class keeps us in sync no matter who else mutated the slides.
      var slides = Array.from(this.querySelectorAll('.product-card__carousel-slide'));
      if (slides.length <= 1) return;

      var current = slides.findIndex(function (slide) {
        return slide.classList.contains('is-active');
      });
      if (current < 0) current = 0;

      var count = slides.length;
      var next = (((current + direction) % count) + count) % count; // wrap both directions
      if (next === current) return;

      slides[current].classList.remove('is-active');
      slides[next].classList.add('is-active');
    }
  }

  customElements.define('product-card-carousel', ProductCardCarousel);
})();
